import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { services } from "./data/servicesData";
import Navbar from "./components/navbar";
import Footer from "./components/footer";
import FontLoader from "./components/fontLoader";
import { PageMeta } from "../seo/PageMeta";

const pageGradient =
  "linear-gradient(180deg, #F8FBFF 0%, #E9F5FF 50%, #DCEEFF 100%)";

const contentEase = [0.22, 1, 0.36, 1];

const staggerContainer = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.14,
      delayChildren: 0.06,
    },
  },
  exit: {
    transition: {
      staggerChildren: 0.1,
      staggerDirection: -1,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, easeInOut: contentEase },
  },
  exit: {
    opacity: 0,
    y: -12,
    transition: { duration: 0.3, easeInOut: contentEase },
  },
};

const notFoundTransition = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
  transition: { duration: 0.4, ease: contentEase },
};

function resolveService(serviceId, locationState) {
  return locationState ?? services.find((s) => String(s.id) === String(serviceId));
}

function NavArrow({ direction }) {
  return direction === "prev" ? (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  ) : (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

function ServiceNavButton({ service, direction, onNavigate }) {
  const label =
    direction === "prev"
      ? `Service précédent : ${service.title}`
      : `Service suivant : ${service.title}`;

  return (
    <motion.button
      type="button"
      onClick={() => onNavigate(service)}
      aria-label={label}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#0C1925]/15 bg-white/80 text-[#0C1925] shadow-[0_8px_24px_rgba(12,25,37,0.1)] transition-colors hover:border-[#0C1925]/25 hover:bg-white hover:shadow-[0_10px_28px_rgba(12,25,37,0.16)]"
    >
      <NavArrow direction={direction} />
    </motion.button>
  );
}

export default function InfoService() {
  const { state } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [activeService, setActiveService] = useState(() =>
    resolveService(id, state)
  );

  useEffect(() => {
    setActiveService(resolveService(id, state));
  }, [id, state]);

  const goToService = useCallback(
    (target) => {
      setActiveService(target);
      navigate(`/infoService/${target.id}`, {
        replace: true,
        state: target,
      });
    },
    [navigate]
  );

  const service = activeService;
  const currentIndex = service
    ? services.findIndex((s) => s.id === service.id)
    : -1;
  const prevService = currentIndex > 0 ? services[currentIndex - 1] : null;
  const nextService =
    currentIndex >= 0 && currentIndex < services.length - 1
      ? services[currentIndex + 1]
      : null;

  return (
    <>
      {service && (
        <PageMeta
          title={service.title}
          description={service.description}
          path={`/infoService/${service.id}`}
        />
      )}
      <FontLoader />
      <Navbar />
      <main style={{ fontFamily: "'Inter', sans-serif" }}>
        <section
          className="min-h-screen pt-20 pb-24 px-6 md:pt-20"
          style={{ background: pageGradient }}
        >
          <motion.div className="mx-auto flex w-full max-w-3xl flex-col items-center text-center">
            {!service ? (
              <motion.div
                key="not-found"
                {...notFoundTransition}
                className="flex flex-col items-center"
              >
                <h1 className="text-3xl font-semibold tracking-tight text-[#0C1925] md:text-4xl">
                  Service introuvable
                </h1>
                <p className="mt-4 text-base text-[#1a1a2e]/65 md:text-lg">
                  Ce service n&apos;existe pas ou n&apos;est plus disponible.
                </p>
                <a
                  href="/"
                  className="mt-10 inline-flex items-center justify-center rounded-full bg-[#0C1925] px-8 py-3 text-sm font-medium tracking-wide text-white transition-shadow hover:shadow-[0_8px_30px_rgba(12,25,37,0.28)]"
                >
                  Retour à l&apos;accueil
                </a>
              </motion.div>
            ) : (
              <>
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={service.id}
                    variants={staggerContainer}
                    initial="hidden"
                    animate="show"
                    exit="exit"
                    className="flex w-full flex-col items-center"
                  >
                    {service.image && (
                      <motion.div
                        variants={staggerItem}
                        className="mt-10 w-full max-w-sm p-6 md:max-w-md"
                      >
                        <img
                          src={service.image}
                          alt={service.title}
                          className="mx-auto h-auto w-full max-w-[280px] object-contain md:max-w-[320px]"
                        />
                      </motion.div>
                    )}

                    <motion.p
                      variants={staggerItem}
                      className="mt-4 max-w-2xl text-base leading-relaxed text-[#1a1a2e]/60 md:text-lg"
                    >
                      {service.description}
                    </motion.p>

                    <motion.div
                      variants={staggerItem}
                      className="mt-10 w-full rounded-3xl border border-[#0C1925]/10 bg-white/60 px-6 py-8 shadow-[0_12px_36px_rgba(79,158,207,0.12)] backdrop-blur-sm md:px-10 md:py-10"
                    >
                      <p className="text-base leading-relaxed text-[#1a1a2e]/80 md:text-lg md:leading-relaxed">
                        {service.information}
                      </p>
                    </motion.div>
                  </motion.div>
                </AnimatePresence>

                {(prevService || nextService) && (
                  <nav
                    aria-label="Navigation entre les services"
                    className="mt-12 flex items-center justify-center gap-5"
                  >
                    {prevService ? (
                      <ServiceNavButton
                        service={prevService}
                        direction="prev"
                        onNavigate={goToService}
                      />
                    ) : null}

                    {nextService ? (
                      <ServiceNavButton
                        service={nextService}
                        direction="next"
                        onNavigate={goToService}
                      />
                    ) : null}
                  </nav>
                )}
              </>
            )}
          </motion.div>
        </section>
      </main>
      <Footer />
    </>
  );
}
